import {isNil} from 'ramda';

import {Nullish} from 'shared/web';

// The editor always opens with a block element and closes it. Testing only the opening tag would
// treat `<table> reservation details:` as markup and lose the word and every line break.
const EDITOR_MARKUP_OPENING_REGEX = /^\s*<(p|div|ol|ul|h[1-6]|blockquote|pre|table)[\s>]/i;

const HTML_ESCAPES: Record<string, string> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;',
};

const CHARACTERS_TO_ESCAPE_REGEX = /[&<>"']/g;

/**
 * Prepares a stored body for the rich text editor.
 *
 * A body written before the editor reached a screen is plain text, and a message template still
 * arrives as plain text with newlines. Handing those over unchanged would collapse every line break
 * into one paragraph, so each line becomes a paragraph and each character in the map is escaped.
 *
 * A value that both opens and closes an editor block element is returned untouched. That is a
 * judgement about its shape, not about its content: this does not sanitize. {@link normalizeEmailHtml}
 * refuses what must not be sent.
 */
export function plainTextToEmailHtml(value: string | Nullish): string {
  if (isNil(value) || value === '') {
    return '';
  }

  if (carriesEditorMarkup(value)) {
    return value;
  }

  return value
    .split(/\r\n|\r|\n/)
    .map((line) => {
      const escaped = line.replace(
        CHARACTERS_TO_ESCAPE_REGEX,
        (character) => HTML_ESCAPES[character]
      );

      return escaped === '' ? '<p><br></p>' : `<p>${escaped}</p>`;
    })
    .join('');
}

function carriesEditorMarkup(value: string) {
  const openingTagName = EDITOR_MARKUP_OPENING_REGEX.exec(value)?.[1]?.toLowerCase();

  // A plain search, not a built pattern: `</p>` must not be satisfied by `</pre>`.
  return !isNil(openingTagName) && value.toLowerCase().includes(`</${openingTagName}>`);
}
